import React, { useState } from 'react';

import { Button, Tooltip } from '@mui/material';
import type { ButtonProps, SxProps, Theme } from '@mui/material';

import PermissionedComponent from '~/components/Common/PermissionedComponent';

import { ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER } from '~/consts/roles';

import useCurrentUser from '~/hooks/useCurrentUser';

import { canSendPreCallSms } from '~/utils/canSendPreCallSms';
import { buildCustomerSmsPhoneNumber } from '~/utils/preCallSms';

import SendPreCallSmsDialog, { type PreCallSmsCustomer } from './SendPreCallSmsDialog';

interface Props {
  user: PreCallSmsCustomer;
  buttonVariant?: ButtonProps['variant'];
  buttonSize?: ButtonProps['size'];
  buttonColor?: ButtonProps['color'];
  buttonSx?: SxProps<Theme>;
}

export default function SendPreCallSmsButton({
  user,
  buttonVariant = 'outlined',
  buttonSize = 'medium',
  buttonColor,
  buttonSx,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const { user: currentUser } = useCurrentUser();
  const hasPhoneNumber = Boolean(buildCustomerSmsPhoneNumber(user.phone_prefix, user.phone));
  const tooltipTitle = hasPhoneNumber
    ? 'Send a pre-call SMS so the customer recognises your outbound call'
    : 'Customer has no mobile number on file';

  // Limited rollout: only the trial group sees the button, on top of the role check below.
  if (!canSendPreCallSms(currentUser)) {
    return null;
  }

  return (
    <PermissionedComponent requiredRoles={[ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER]}>
      <>
        <Tooltip title={tooltipTitle}>
          <span>
            <Button
              variant={buttonVariant}
              size={buttonSize}
              color={buttonColor}
              onClick={() => setIsOpen(true)}
              disabled={!hasPhoneNumber}
              sx={buttonSx}
            >
              Send pre-call SMS
            </Button>
          </span>
        </Tooltip>
        <SendPreCallSmsDialog open={isOpen} onClose={() => setIsOpen(false)} customer={user} />
      </>
    </PermissionedComponent>
  );
}
